import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import FileUpload from '../FileUpload';
import Toast from '../../UI/Toast';

const UploadContent = () => {
  const location = useLocation();
  const fileUploadRef = useRef(null);
  
  // Parse query params for prefill
  const getPrefill = () => {
    const params = new URLSearchParams(location.search);
    return {
      firstName: params.get('firstName') || '',
      middleName: params.get('middleName') || '',
      lastName: params.get('lastName') || '',
      mobile: params.get('mobile') || '',
      email: params.get('email') || '',
    };
  };
  
  const [formData, setFormData] = useState(getPrefill());
  const [mobileError, setMobileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  
  // Toast state
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'error'
  });

  useEffect(() => {
    setFormData(getPrefill());
    // eslint-disable-next-line
  }, [location.search]);

  // Debug toast state changes
  useEffect(() => {
    console.log('Toast state changed:', toast);
  }, [toast]);

  const showToast = (message, type = 'error') => {
    console.log('Showing toast:', message, type);
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  const hideToast = () => {
    console.log('Hiding toast');
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const clearForm = () => {
    // Reset form data
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      mobile: '',
      email: ''
    });
    
    // Clear validation errors
    setMobileError('');
    
    // Clear the file upload component
    if (fileUploadRef.current) {
      fileUploadRef.current.removeFile();
    }
    
    // Reset submitting state
    setIsSubmitting(false);
    
    // Force form re-render by incrementing key
    setFormKey(prev => prev + 1);
    
    // Clear any existing toast after a delay
    setTimeout(() => {
      hideToast();
    }, 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      // Only allow numbers
      if (!/^\d*$/.test(value)) return;
      // Limit to 10 digits
      if (value.length > 10) return;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Live validation
      if (value.length !== 10 && value.length !== 0) {
        setMobileError('Mobile number must be exactly 10 digits.');
      } else {
        setMobileError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileSelected = (file) => {
    // Clear any existing toast when file is selected
    hideToast();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.mobile || !formData.email) {
      showToast('Please fill all required fields.', 'error');
      return;
    }
    
    if (formData.mobile.length !== 10) {
      setMobileError('Mobile number must be exactly 10 digits.');
      showToast('Please enter a valid 10-digit mobile number.', 'error');
      return;
    }

    // Check if file is selected
    if (!fileUploadRef.current || !fileUploadRef.current.selectedFile) {
      showToast('Please select an article file.', 'error');
      return;
    }

    setIsSubmitting(true);
    
    // Show uploading toast immediately
    showToast('Uploading file...', 'warning');

    try {
      // Upload the file first
      const uploadResult = await fileUploadRef.current.uploadFile();
      
      if (uploadResult && uploadResult.success && uploadResult.data) {
        // Clear the form immediately after successful upload
        clearForm();
        
        // Check if email was sent
        const emailSent = uploadResult.data.emailSent;
        const emailMessage = emailSent 
          ? 'File uploaded successfully! Confirmation email sent to your inbox.'
          : 'File uploaded successfully! Email delivery may be delayed.';
        
        // Show success toast after a small delay
        setTimeout(() => {
          showToast(emailMessage, 'success');
        }, 100);
        
      } else {
        showToast('File upload failed. Please try again.', 'error');
      }
    } catch (error) {
      console.error('UploadContent: Submit error:', error);
      showToast('Submission failed. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-fadein min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Modern Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent to-blue-600 rounded-2xl mb-6 shadow-lg">
            <i className="bi bi-cloud-upload text-2xl text-white"></i>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-accent to-blue-600 bg-clip-text text-transparent mb-4">
            Submit Article
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Submit your research paper to AIM Journal of Homoeopathic Medicine. We're committed to advancing homoeopathic research and clinical practice.
          </p>
        </div>

        {/* Modern Form Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden">
          <form key={formKey} onSubmit={handleSubmit} className="p-8 space-y-8">
            
            {/* Author Information Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-accent to-blue-600 rounded-lg flex items-center justify-center">
                  <i className="bi bi-person text-white text-sm"></i>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Author Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="firstName" 
                    placeholder="Enter first name" 
                    value={formData.firstName} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-accent/20 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-400" 
                    required 
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Middle Name
                  </label>
                  <input 
                    type="text" 
                    name="middleName" 
                    placeholder="Enter middle name" 
                    value={formData.middleName} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-accent/20 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-400" 
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="lastName" 
                    placeholder="Enter last name" 
                    value={formData.lastName} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-accent/20 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-400" 
                    required 
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <i className="bi bi-telephone text-white text-sm"></i>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Contact Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="mobile" 
                    placeholder="Enter 10-digit mobile number" 
                    value={formData.mobile} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-accent/20 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-400" 
                    required 
                    maxLength={10} 
                  />
                  {mobileError && <div className="text-sm text-red-500 mt-1 flex items-center space-x-1">
                    <i className="bi bi-exclamation-circle"></i>
                    <span>{mobileError}</span>
                  </div>}
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Enter email address" 
                    value={formData.email} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-accent/20 focus:bg-white transition-all duration-200 text-gray-800 placeholder-gray-400" 
                    required 
                  />
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <i className="bi bi-file-earmark-arrow-up text-white text-sm"></i>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Article File</h2>
              </div>
              
              <FileUpload 
                ref={fileUploadRef}
                folder="article-submissions"
                onFileSelected={handleFileSelected}
                userEmail={formData.email}
                formData={formData}
              />
            </div>

            {/* Submission Guidelines */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                  <i className="bi bi-list-check text-white text-sm"></i>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Submission Guidelines</h2>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <p className="text-sm text-gray-700 mb-4 font-medium">
                  Please ensure you have the following documents ready for submission:
                </p>
                <div className="space-y-3">
                  {[
                    "Initial File (Covering letter, acknowledgements, declaration of interest, funding details, reference number and date of approval from Institutional Ethics Committee)",
                    "Title Page (Word File)",
                    "Main File (including abstract and key words, introduction, material and methods, results/observation, discussion, conclusions, references and tables)",
                    "Figures and Figure Legends (if applicable)",
                    "Contributors' Form (Scanned signed copy)"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-accent to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
           
            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-8 rounded-full font-semibold text-lg transition-all duration-300 transform ${
                  !isSubmitting 
                    ? 'bg-gradient-to-r from-accent to-blue-600 hover:from-accent-dark hover:to-blue-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <i className="bi bi-send"></i>
                    <span>Submit Article</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Toast Component */}
      {console.log('Rendering toast with state:', toast)}
      {toast.isVisible && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast} 
          isVisible={toast.isVisible}

        />
      )}
    </div>
  );
};

export default UploadContent; 